import { AppColors } from "@/styles/colors";
import { Field, Text } from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { Control, Controller } from "react-hook-form";
import CreatableSelect from "react-select/creatable";
import { Option } from "@/components/select/AsyncAutocompleteSelect";

const customStyles = {
  container: (provided: any) => ({
    ...provided,
    width: "100%",
    minHeight: "40px",
  }),
  control: (provided: any, state: any) => ({
    ...provided,
    backgroundColor: state.isFocused ? "#f0f0f0" : "white",
    borderColor: state.isFocused ? "#007bff" : "#ccc",
    boxShadow: state.isFocused ? "0 0 5px rgba(0, 123, 255, 0.5)" : "none",
    "&:hover": {
      borderColor: "#007bff",
    },
    minHeight: "40px",
  }),
  option: (provided: any, state: any) => {
    const isFocusedColor = state.isFocused ? "#cce5ff" : "white";
    return {
      ...provided,
      backgroundColor: state.isSelected ? "#007bff" : isFocusedColor,
      color: state.isSelected ? "white" : "black",
      padding: 10,
    };
  },
  menu: (provided: any) => ({
    ...provided,
    borderRadius: "8px",
    boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
    zIndex: 9999,
  }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
};

interface Props {
  control: Control<any>;
  name: string;
  labelText?: string;
  placeholder?: string;
  items: Option[];
  loading?: boolean;
  disabled?: boolean;
  required?: boolean;
}

export const CustomCreatableSelect = ({
  control,
  name,
  labelText,
  placeholder,
  items,
  loading,
  disabled,
  required,
}: Props) => {
  const [inputValue, setInputValue] = useState<string>("");

  const options = useMemo(() => {
    return (items ?? []).map((o) => ({
      label: String(o.label),
      value: String(o.value),
    }));
  }, [items]);

  const getSelectedOption = (raw: any) => {
    const v = String(raw ?? "").trim();
    if (!v) return null;
    const matched = options.find((o) => String(o.value) === v);
    if (matched) return matched;
    return { label: v, value: v };
  };

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const selected = getSelectedOption(field.value);
        const error = fieldState.error;

        return (
          <Field.Root invalid={!!error} width="100%">
            {labelText && (
              <Field.Label>
                <Text display="flex" gap="4px" fontWeight={600}>
                  {labelText}{" "}
                  {required && (
                    <Text as="span" fontWeight={600} color={AppColors.REQUIRED}>
                      *
                    </Text>
                  )}
                </Text>
              </Field.Label>
            )}

            <CreatableSelect
              menuPortalTarget={document.body}
              styles={customStyles}
              isClearable
              isDisabled={disabled}
              isLoading={loading}
              placeholder={placeholder}
              options={options}
              value={selected}
              inputValue={inputValue}
              formatCreateLabel={(val) => `Agregar: ${val}`}
              onInputChange={(val, meta) => {
                if (meta.action === "input-change") setInputValue(val);
              }}
              onChange={(val) => {
                const next = (val as any)?.value ? String((val as any).value) : "";
                field.onChange(next);
                setInputValue("");
              }}
              onCreateOption={(val) => {
                const v = String(val ?? "").trim();
                field.onChange(v);
                setInputValue("");
              }}
              onBlur={() => {
                field.onBlur();
                const v = String(inputValue ?? "").trim();
                if (v && !String(field.value ?? "").trim()) {
                  field.onChange(v);
                  setInputValue("");
                }
              }}
            />

            {!!error && <Field.ErrorText>{String(error?.message)}</Field.ErrorText>}
          </Field.Root>
        );
      }}
    />
  );
};
